import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutGrid,
    Briefcase,
    FileText,
    ClipboardList,
    GraduationCap,
    School,
    Users,
    MessageCircle,
    History,
    Download,
    BarChart3,
    KeyRound,
    LogOut,
    Menu,
    X,
} from "lucide-react";
import { logout } from "@/lib/auth";
import { toast } from "sonner";

const NAV = [
    {
        to: "/bbrgadminacc/modules",
        label: "Modules",
        icon: LayoutGrid,
        testid: "sidebar-modules",
    },
    {
        to: "/bbrgadminacc/job-role",
        label: "Create Job Role",
        icon: Briefcase,
        testid: "sidebar-job-role",
    },
    {
        to: "/bbrgadminacc/job-description",
        label: "Create Job Description",
        icon: FileText,
        testid: "sidebar-job-description",
    },
    {
        to: "/bbrgadminacc/form",
        label: "Create Form",
        icon: ClipboardList,
        testid: "sidebar-form",
    },
    {
        to: "/bbrgadminacc/degrees",
        label: "Manage Degrees",
        icon: GraduationCap,
        testid: "sidebar-degrees",
    },
    {
        to: "/bbrgadminacc/colleges",
        label: "Manage Colleges",
        icon: School,
        testid: "sidebar-colleges",
    },
    {
        to: "/bbrgadminacc/job-role-master",
        label: "Job Role Master",
        icon: Briefcase,
        testid: "sidebar-job-role-master",
    },
    {
        to: "/bbrgadminacc/attendance-report",
        label: "Attendance Report",
        icon: BarChart3,
        testid: "sidebar-attendance-report",
    },
    {
        to: "/bbrgadminacc/candidates",
        label: "Candidate WhatsApp",
        icon: MessageCircle,
        testid: "sidebar-candidates",
    },
    {
        to: "/bbrgadminacc/whatsapp-logs",
        label: "WhatsApp Logs",
        icon: History,
        testid: "sidebar-whatsapp-logs",
    },
    {
        to: "/bbrgadminacc/attendance-export",
        label: "Attendance Export",
        icon: Download,
        testid: "sidebar-attendance-export",
    },
    {
        to: "/bbrgadminacc/applicants",
        label: "View Applicants",
        icon: Users,
        testid: "sidebar-applicants",
    },
    {
        to: "/bbrgadminacc/change-password",
        label: "Change Password",
        icon: KeyRound,
        testid: "sidebar-change-password",
    },
];

function SidebarBody({ onNavigate }) {
    const navigate = useNavigate();
    const handleLogout = () => {
        logout();
        toast.success("Signed out");
        navigate("/bbrgadminacc", { replace: true });
    };

    return (
        <div className="flex flex-col h-full">
            <div className="px-6 pt-8 pb-6">
                <img
                    src="/blubridge-logo.png"
                    alt="BluBridge"
                    className="h-6 md:h-7 w-auto"
                    style={{ filter: "brightness(0) invert(1)" }}
                />
                <div className="mt-2 text-[10px] tracking-label uppercase text-[#F7F5F0]/50">
                    Admin Console
                </div>
            </div>

            <nav className="flex-1 px-3 space-y-1">
                {NAV.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                        key={to}
                        to={to}
                        onClick={onNavigate}
                        data-testid={testid}
                        end
                        className={({ isActive }) =>
                            `group flex items-center gap-3 px-4 py-3 text-sm font-medium border-l-2 transition-colors duration-200 ${
                                isActive
                                    ? "bg-white/[0.06] border-[#F7F5F0] text-[#F7F5F0]"
                                    : "border-transparent text-[#F7F5F0]/65 hover:text-[#F7F5F0] hover:bg-white/[0.03]"
                            }`
                        }
                    >
                        <Icon size={16} strokeWidth={1.5} />
                        <span>{label}</span>
                    </NavLink>
                ))}
            </nav>

            <div className="p-3 border-t border-white/10">
                <button
                    onClick={handleLogout}
                    data-testid="sidebar-logout"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#F7F5F0]/70 hover:text-[#F7F5F0] hover:bg-white/[0.04] transition-colors duration-200"
                >
                    <LogOut size={16} strokeWidth={1.5} />
                    Logout
                </button>
            </div>
        </div>
    );
}

export default function AdminLayout() {
    const [mobileOpen, setMobileOpen] = useState(false);

    return (
        <div
            data-testid="admin-shell"
            className="min-h-screen bg-[#F3F4F6] text-[#0F1F38] font-sans flex"
        >
            {/* Desktop sidebar */}
            <aside
                data-testid="admin-sidebar-desktop"
                className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#0F1F38] text-[#F7F5F0] border-r border-white/10 flex-col"
            >
                <SidebarBody />
            </aside>

            {/* Mobile top bar */}
            <div
                data-testid="admin-mobile-topbar"
                className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-[#0F1F38] text-[#F7F5F0] flex items-center justify-between px-4 border-b border-white/10"
            >
                <img
                    src="/blubridge-logo.png"
                    alt="BluBridge"
                    className="h-6 w-auto"
                    style={{ filter: "brightness(0) invert(1)" }}
                />
                <button
                    onClick={() => setMobileOpen(true)}
                    data-testid="admin-menu-open"
                    className="p-2 hover:bg-white/10 rounded"
                    aria-label="Open menu"
                >
                    <Menu size={20} />
                </button>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div
                    data-testid="admin-mobile-drawer"
                    className="lg:hidden fixed inset-0 z-50 flex"
                >
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setMobileOpen(false)}
                    />
                    <aside className="relative w-72 h-full bg-[#0F1F38] text-[#F7F5F0]">
                        <button
                            onClick={() => setMobileOpen(false)}
                            data-testid="admin-menu-close"
                            className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded"
                            aria-label="Close menu"
                        >
                            <X size={20} />
                        </button>
                        <SidebarBody
                            onNavigate={() => setMobileOpen(false)}
                        />
                    </aside>
                </div>
            )}

            {/* Main workspace */}
            <div className="flex-1 lg:pl-64 pt-14 lg:pt-0 min-h-screen">
                <main className="min-h-screen">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
